import { Controller, useFieldArray, useWatch, type Control, type UseFormRegister } from 'react-hook-form';
import { Plus, Trash2, Wand2 } from 'lucide-react';
import { Button, DateField, IconButton, NumberField, Select, TextField } from '@/components';
import { formatNumber } from '@/lib/format';
import type { PreviewResult } from '../api';
import { dayRow, ZONE_SLOTS, type VoyageForm } from '../form';
import '../voyage-reports.css';

const CLASS_OPTIONS = ['S', 'A', 'B'].map((v) => ({ value: v, label: v }));

/** 速報 調査内容（調査日・調査時間・調査水域×10・調査判定・感想） */
export function SurveyDaysTable({
  control,
  register,
  calc,
  onAuto,
  onZonesChange,
  errors,
}: {
  control: Control<VoyageForm>;
  register: UseFormRegister<VoyageForm>;
  calc: PreviewResult['calc'] | null;
  onAuto: () => void;
  /** 調査水域を変更した（行番号）。調査判定の自動更新に使う */
  onZonesChange?: (rowIndex: number) => void;
  errors?: Record<number, Record<string, { message?: string } | undefined> | undefined>;
}) {
  const rows = useFieldArray({ control, name: 'surveyDays' });
  const values = useWatch({ control, name: 'surveyDays' });
  const byDate = new Map((calc?.surveyDays ?? []).map((d) => [d.surveyDate, d]));
  const totals = (calc?.surveyDays ?? []).reduce((a, d) => ({ wage: a.wage + d.wageTotal, charter: a.charter + d.charterTotal }), { wage: 0, charter: 0 });
  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table className="bs-survey-table">
          <thead>
            <tr>
              <th style={{ width: 150 }}>調査日</th>
              <th style={{ width: 96 }}>
                調査時間計
                <br />
                （時間）
              </th>
              <th>調査水域（農林漁区番号）</th>
              <th style={{ width: 90 }}>
                調査判定
                <br />
                （SAB）
              </th>
              <th className="is-num" style={{ width: 110 }}>
                賃金
              </th>
              <th className="is-num" style={{ width: 110 }}>
                用船料
              </th>
              <th>調査全般に関する感想</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.fields.map((f, i) => {
              const v = values?.[i];
              const c = v?.surveyDate ? byDate.get(v.surveyDate) : undefined;
              const e = errors?.[i];
              return (
                <tr key={f.id}>
                  <td>
                    <DateField aria-label="調査日" invalid={!!e?.surveyDate} {...register(`surveyDays.${i}.surveyDate`)} />
                  </td>
                  <td>
                    <Controller control={control} name={`surveyDays.${i}.surveyHours`} render={({ field }) => <NumberField {...field} decimals={1} unit="時間" width="full" aria-label="調査時間計（時間）" />} />
                  </td>
                  <td>
                    <div className="is-zones">
                      {Array.from({ length: ZONE_SLOTS }, (_, k) => (
                        <Controller
                          key={k}
                          control={control}
                          name={`surveyDays.${i}.zones.${k}`}
                          render={({ field }) => (
                            <input
                              className="bs-input"
                              inputMode="numeric"
                              aria-label={`調査水域${k + 1}`}
                              value={field.value ?? ''}
                              onChange={(ev) => {
                                const t = ev.target.value.normalize('NFKC').replace(/[^0-9]/g, '');
                                field.onChange(t === '' ? null : Number(t));
                                onZonesChange?.(i);
                              }}
                            />
                          )}
                        />
                      ))}
                    </div>
                    {c && c.zoneClasses.some(Boolean) && (
                      <div className="bs-zone-cls" aria-label="調査水域の区分">
                        {c.zoneClasses.map((z, k) => (
                          <span key={k} className={`bs-cls is-${z || 'none'}`} title={`${c.zoneNumbers[k]}`}>
                            {z || '－'}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    <Select placeholder="—" options={CLASS_OPTIONS} invalid={!!e?.surveyClass} aria-label="調査判定" {...register(`surveyDays.${i}.surveyClass`)} />
                  </td>
                  <td className="is-num">{c ? formatNumber(c.wageTotal) : '—'}</td>
                  <td className="is-num">{c ? formatNumber(c.charterTotal) : '—'}</td>
                  <td className="is-impression">
                    <TextField aria-label="調査全般に関する感想" {...register(`surveyDays.${i}.impression`)} />
                  </td>
                  <td>
                    <IconButton label="行を削除" variant="danger" size="sm" icon={<Trash2 size={14} />} onClick={() => rows.remove(i)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
          {calc && (
            <tfoot>
              <tr className="bs-survey-total">
                <td colSpan={4}>
                  合計（乗船人員 {calc.crewCount}人・実作業日数 {formatNumber(calc.actualWorkDays, 2)}日・消費税 {calc.hasConsumptionTax ? `有り ${Math.round(calc.taxRate * 100)}%` : '無し'}）
                </td>
                <td className="is-num">{formatNumber(totals.wage)}</td>
                <td className="is-num">{formatNumber(totals.charter)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Button size="sm" icon={<Plus size={15} />} onClick={() => rows.append(dayRow(''))}>
          行を追加
        </Button>
        <Button size="sm" variant="accent" icon={<Wand2 size={15} />} onClick={onAuto} title="全行の調査水域から最上位の区分（S>A>B）を求め、全行の調査判定に設定します">
          自動（SAB判定）
        </Button>
      </div>
    </>
  );
}
